import React, { useState, useEffect } from 'react'
import { api } from '../api'
import ExportButton from '../components/ExportButton'
import LastUpdated from '../components/LastUpdated'

const statusColors = {
  'Dev': '#60A5FA', 'RFC': '#FBBF24', 'External Flight': '#34D399',
  'Beta Flight': '#34D399', 'Not started': '#9CA3AF', 'Cut': '#F87171',
  'GA': '#34D399', 'TBD': '#9CA3AF',
}

const AVATAR_COLORS = ['#0078D4', '#8764B8', '#107C10', '#FF8C00', '#D13438', '#00BCD4', '#E91E63', '#3F51B5']

export default function Members() {
  const [members, setMembers] = useState([])
  const [workItems, setWorkItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null) // member id
  const [editForm, setEditForm] = useState({ fullName: '', role: '' })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    Promise.all([api.getSquadMembers(), api.getWorkItems()])
      .then(([m, w]) => { setMembers(m); setWorkItems(w) })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const startEdit = (member) => {
    setEditing(member.id)
    setEditForm({ fullName: member.fullName || '', role: member.role || '' })
  }

  const cancelEdit = () => { setEditing(null) }

  const saveEdit = async (memberId) => {
    setSaving(true)
    try {
      const updated = await api.updateSquadMember(memberId, editForm)
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, ...editForm } : m))
      setEditing(null)
    } catch (e) { console.error(e) }
    setSaving(false)
  }

  if (loading) return <div className="loading">Loading members...</div>

  const getMemberItems = (member) => workItems.filter(wi => wi.owner === member.email)

  const getStatusBreakdown = (items) => {
    const counts = {}
    items.forEach(i => { counts[i.status] = (counts[i.status] || 0) + 1 })
    return counts
  }

  return (
    <div className="members-page fade-in">
      <div className="page-header">
        <h2>Squad Members</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <LastUpdated data={members} />
          <ExportButton data={members} filename="members" tabType="members" />
        </div>
      </div>
      <div className="members-grid">
        {members.map((member, idx) => {
          const memberItems = getMemberItems(member)
          const breakdown = getStatusBreakdown(memberItems)
          const isEditing = editing === member.id
          return (
            <div key={member.id} className="member-card">
              <div className="member-header">
                <div className="member-avatar" style={{ background: AVATAR_COLORS[idx % AVATAR_COLORS.length] }}>
                  {member.fullName?.charAt(0) || '?'}
                </div>
                <div style={{ flex: 1 }}>
                  {isEditing ? (
                    <div className="member-edit-form">
                      <input type="text" value={editForm.fullName} onChange={e => setEditForm(f => ({ ...f, fullName: e.target.value }))} placeholder="Full Name" className="edit-input" />
                      <input type="text" value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))} placeholder="Role" className="edit-input" style={{ marginTop: 4 }} />
                      <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                        <button className="btn-sm" onClick={() => saveEdit(member.id)} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                        <button className="btn-sm" onClick={cancelEdit}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h3>{member.fullName}</h3>
                      <span className="member-role">{member.role} · {member.id}</span>
                    </>
                  )}
                </div>
                {!isEditing && (
                  <button className="btn-sm member-edit-btn" onClick={() => startEdit(member)}>Edit</button>
                )}
              </div>
              <div className="member-stats">
                <span className="stat-total">{memberItems.length} items</span>
                {Object.entries(breakdown).map(([status, count]) => (
                  <span key={status} className="stat-item">
                    <span className="status-dot" style={{ background: statusColors[status] || '#999', marginRight: 4 }} />
                    {status}: {count}
                  </span>
                ))}
              </div>
              <div className="member-items">
                {memberItems.map(wi => (
                  <div key={wi.id} className="member-work-item">
                    <span className="status-dot" style={{ background: statusColors[wi.status] || '#999' }} />
                    <span className="wi-title">{wi.title}</span>
                    <span className={`badge-sm priority-${wi.priority?.toLowerCase()}`}>{wi.priority}</span>
                    <span className="wi-status">{wi.status}</span>
                  </div>
                ))}
                {memberItems.length === 0 && <div className="empty-items">No work items assigned</div>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
